/**
 * API Client Service
 * ------------------
 * Centralized HTTP client for the API Gateway at /api.
 * Automatically attaches Firebase Auth bearer token to all requests.
 * Since web app is on the same domain (travelez.app), uses relative URLs — no CORS needed.
 */

import { auth } from '../config/firebase';

const BASE_URL = '/api';

/**
 * Same gateway, reached without going through Firebase Hosting.
 *
 * A Hosting rewrite gives up on the origin after 60 seconds and answers 502
 * even though the function is still working and eventually succeeds (itinerary
 * parsing routinely takes 70–90s). Calls that can outlast that must talk to the
 * function directly, where the function's own `timeoutSeconds` applies; the
 * gateway already allows this origin in its CORS config. The doubled `/api` is
 * correct — the first segment is the function name, the second the Express
 * mount point.
 */
const DIRECT_BASE_URL =
    'https://us-central1-travel-ez-7ee91.cloudfunctions.net/api/api';

async function getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };
}

export class ApiClient {
    /**
     * GET request to the gateway.
     */
    static async get<T = any>(path: string): Promise<T> {
        const headers = await getAuthHeaders();
        const response = await fetch(`${BASE_URL}${path}`, { headers });
        if (!response.ok) {
            const body = await response.text();
            throw new Error(`API GET ${path} failed (${response.status}): ${body}`);
        }
        return response.json();
    }

    /**
     * GET request that returns raw Response (for binary data like images).
     */
    static async getRaw(path: string): Promise<Response> {
        const headers = await getAuthHeaders();
        return fetch(`${BASE_URL}${path}`, { headers });
    }

    /**
     * POST request to the gateway.
     *
     * Set `longRunning` for calls that can take longer than a minute so they
     * skip the Hosting rewrite (see {@link DIRECT_BASE_URL}).
     */
    static async post<T = any>(
        path: string,
        body?: unknown,
        options?: { longRunning?: boolean },
    ): Promise<T> {
        const headers = await getAuthHeaders();
        const base = options?.longRunning ? DIRECT_BASE_URL : BASE_URL;
        const response = await fetch(`${base}${path}`, {
            method: 'POST',
            headers,
            body: body != null ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`API POST ${path} failed (${response.status}): ${text}`);
        }
        return response.json();
    }

    /**
     * PUT request to the gateway.
     */
    static async put<T = any>(path: string, body?: unknown): Promise<T> {
        const headers = await getAuthHeaders();
        const response = await fetch(`${BASE_URL}${path}`, {
            method: 'PUT',
            headers,
            body: body != null ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`API PUT ${path} failed (${response.status}): ${text}`);
        }
        return response.json();
    }

    /** Base URL for constructing public image URLs (e.g. photo proxy). */
    static readonly baseUrl = BASE_URL;
}
